/**
 * Client portal « Mes Rendez-vous » (W5, B3-ClientMeetings, QW-h5, QW-h10,
 * DES-11, DES-12, DES-20).
 *
 * The meetings booked for the client (ParallelCall with a booked meeting),
 * read page by page from the server (`POST /api/entities/ParallelCall/page`,
 * 20 a page, cursor), never truncated (the past list used to stop silently
 * at 10). Three tabs:
 *   - « À valider »: the portal's one list of meetings to validate (booked,
 *     validation pending, still to come: REV-W5-6, usePortalAwaitingMeetings,
 *     the same list and count as the home card and the menu badge), paged
 *     here 20 a page; accept / decline / reschedule;
 *   - « Confirmés » and « Tous », each its own server filter, total and
 *     cursor pages. Changing tab starts on page 1 with no cursor in the same
 *     update (REV-W5-8: no request with the previous tab's cursor).
 * The meeting time is `meeting_date`, else `connected_at` when it is absent
 * (PORTAIL-W5-1, same rule as the home tab). « Rejoindre » opens the event's
 * own link (`calendar_event_url`, http(s) only) and is not shown without one.
 * A table instead of animated cards (DES-11, DES-20); every action reports
 * its failure instead of failing silently.
 */
import { meetingTime, BOOKED_MEETING_FILTER, PENDING_VALIDATION_FILTER } from '@/lib/portalMeetings';
import { usePortalAwaitingMeetings, PORTAL_AWAITING_QUERY_KEY } from '@/hooks/usePortalAwaitingMeetings';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { api } from '@/api/api-client';
import { ApiErrorException } from '@/api/client';
import { fetchEntityPage } from '@/api/endpoints/entityPage';
import { useClientAuth } from '@/hooks/useClientAuth';

export const MEETINGS_PAGE_SIZE = 20;

interface ValidationHistoryEntry { action: string; note?: string; date: string; actor: string }

export interface MeetingRow {
  id: string;
  prospect_name?: string | null;
  prospect_company?: string | null;
  connected_at?: string | null;
  meeting_date?: string | null;
  meeting_status?: string | null;
  calendar_event_url?: string | null;
  client_validation_status?: string | null;
  validation_history?: ValidationHistoryEntry[];
}

interface ApiShape {
  entities: {
    ParallelCall: { update(id: string, patch: Record<string, unknown>): Promise<unknown> };
    ClientNotification: { create(data: Record<string, unknown>): Promise<unknown> };
  };
  integrations: { Core: { SendEmail: (args: { to: string; subject: string; body: string }) => Promise<unknown> } };
  functions: { invoke: (name: string, params: Record<string, unknown>) => Promise<{ status?: number }> };
}

interface MeetingsSectionProps {
  /** Kept for ClientDashboard; the meetings are read from the server. */
  calls?: unknown[];
  clientEmail?: string;
}

/** When the meeting takes place: one rule for the whole portal (PORTAIL-W5-1). */
export function meetingAt(m: Pick<MeetingRow, 'meeting_date' | 'connected_at'>): Date | null {
  return meetingTime(m);
}

/** The event's own link, only when it is a web address. */
export function joinUrl(m: Pick<MeetingRow, 'calendar_event_url'>): string | null {
  const raw = (m.calendar_event_url || '').trim();
  return /^https?:\/\//i.test(raw) ? raw : null;
}

const BOOKED = BOOKED_MEETING_FILTER;
const PENDING = PENDING_VALIDATION_FILTER;

export type MeetingsTab = 'pending' | 'confirmed' | 'all';
export function meetingsFilter(tab: MeetingsTab): Record<string, unknown> {
  if (tab === 'pending') return { $and: [BOOKED, PENDING] };
  if (tab === 'confirmed') return { $and: [BOOKED, { client_validation_status: 'confirmed' }] };
  return BOOKED;
}

const MEETING_STATUSES = ['scheduled', 'completed', 'no_show', 'new_client', 'cancelled', 'rescheduled'] as const;
const STATUS_KEY: Record<string, string> = {
  scheduled: 'scheduled', completed: 'completed', no_show: 'noShow', new_client: 'newClient', cancelled: 'cancelled', rescheduled: 'rescheduled',
};
const codeOf = (err: unknown) => (err instanceof ApiErrorException ? err.code : 'NETWORK');

export default function MeetingsSection({ clientEmail }: MeetingsSectionProps) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const { client } = useClientAuth();
  const [tab, setTab] = useState<MeetingsTab>('pending');
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [busy, setBusy] = useState<string | null>(null);
  const filter = useMemo(() => meetingsFilter(tab), [tab]);

  // REV-W5-8: page and cursors reset with the tab, in the same update.
  const selectTab = (next: MeetingsTab) => {
    if (next === tab) return;
    setTab(next);
    setPage(0);
    setCursors([undefined]);
  };

  // « À valider »: the shared list (one rule for the whole portal).
  const awaiting = usePortalAwaitingMeetings({ scope: clientEmail ?? null });
  const onPendingTab = tab === 'pending';

  // « Confirmés » / « Tous »: server pages.
  const query = useQuery({
    queryKey: ['portal-meetings', clientEmail ?? null, tab, cursors[page] ?? null],
    queryFn: ({ signal }) => fetchEntityPage<MeetingRow>('ParallelCall', {
      filter, sort: '-created_date', limit: MEETINGS_PAGE_SIZE, include_total: true, ...(cursors[page] ? { cursor: cursors[page] } : {}),
    }, signal),
    enabled: !onPendingTab,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (codeOf(query.error) === 'INVALID_CURSOR' && page > 0) { setPage(0); setCursors([undefined]); }
  }, [query.error, page]);

  const shown = onPendingTab ? awaiting.query : query;
  const rows: MeetingRow[] = onPendingTab
    ? (awaiting.rows as MeetingRow[]).slice(page * MEETINGS_PAGE_SIZE, (page + 1) * MEETINGS_PAGE_SIZE)
    : query.data?.items ?? [];
  const total = onPendingTab
    ? (awaiting.query.isSuccess ? awaiting.rows.length : null)
    : typeof query.data?.total === 'number' ? query.data.total : null;
  const onPageChange = (next: number) => {
    if (!onPendingTab && next > page) {
      const c = query.data?.next_cursor;
      if (!c) return;
      setCursors((prev) => { const copy = prev.slice(0, page + 1); copy[page + 1] = c; return copy; });
    }
    setPage(next);
  };

  const a = api as unknown as ApiShape;
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['portal-meetings'] });
    void queryClient.invalidateQueries({ queryKey: PORTAL_AWAITING_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['parallel-calls-completed'] });
  };
  const history = (m: MeetingRow, action: string): ValidationHistoryEntry[] => [
    ...(m.validation_history || []), { action, note: '', date: new Date().toISOString(), actor: 'client' },
  ];
  const calendlyUrl = (client?.calendly_url as string | undefined) || '';

  /** Runs one action; a failure is said, with its code (DES-16). */
  const run = async (m: MeetingRow, fn: () => Promise<void>, success: string) => {
    setBusy(m.id);
    try {
      await fn();
      toast.success(success);
    } catch (err) {
      toast.error(t('admin:backoffice.meetings.actionFailed'), { description: codeOf(err) });
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const accept = (m: MeetingRow) => run(m, async () => {
    await a.entities.ParallelCall.update(m.id, {
      client_validation_status: 'confirmed', client_validation_date: new Date().toISOString(), validation_history: history(m, 'confirmed'),
    });
    await a.entities.ClientNotification.create({
      notification_id: `meeting_validation_${m.id}_${Date.now()}`,
      title: t('admin:backoffice.meetings.notif.confirmedTitle'),
      message: t('admin:backoffice.meetings.notif.confirmedMessage', { name: m.prospect_name, company: m.prospect_company ? ` - ${m.prospect_company}` : '' }),
      type: 'success',
    }).catch(() => undefined);
    if (client?.email) {
      await a.integrations.Core.SendEmail({
        to: client.email as string,
        subject: t('admin:backoffice.meetings.email.confirmedSubject'),
        body: t('admin:backoffice.meetings.email.confirmedBody', { name: m.prospect_name }),
      }).catch(() => undefined);
    }
    const gEnabled = client?.google_calendar_enabled !== false;
    if (!gEnabled || client?.google_calendar_prefer_calendly) {
      if (calendlyUrl) window.open(calendlyUrl, '_blank', 'noopener');
      return;
    }
    const res = await a.functions.invoke('createGoogleCalendarEvent', {
      summary: `RDV - ${m.prospect_name}${m.prospect_company ? ` - ${m.prospect_company}` : ''}`,
      description: `${m.prospect_name}\n${m.prospect_company || ''}`,
      start: (meetingAt(m) ?? new Date()).toISOString(),
      durationMinutes: 30,
      timeZone: 'Europe/Paris',
      calendarId: (client?.google_calendar_calendar_id as string | undefined) || 'primary',
    }).catch(() => ({ status: 0 }));
    if (res?.status !== 200 && calendlyUrl) window.open(calendlyUrl, '_blank', 'noopener');
  }, t('admin:backoffice.meetings.notif.confirmedTitle'));

  const decline = (m: MeetingRow) => run(m, async () => {
    await a.entities.ParallelCall.update(m.id, {
      client_validation_status: 'declined', client_validation_date: new Date().toISOString(), validation_history: history(m, 'declined'),
    });
    await a.entities.ClientNotification.create({
      notification_id: `meeting_declined_${m.id}_${Date.now()}`,
      title: t('admin:backoffice.meetings.notif.declinedTitle'),
      message: t('admin:backoffice.meetings.notif.declinedMessage', { name: m.prospect_name }),
      type: 'warning',
    }).catch(() => undefined);
  }, t('admin:backoffice.meetings.notif.declinedTitle'));

  const reschedule = (m: MeetingRow) => run(m, async () => {
    await a.entities.ParallelCall.update(m.id, {
      client_validation_status: 'reschedule_requested', client_validation_date: new Date().toISOString(), validation_history: history(m, 'reschedule_requested'),
    });
    await a.entities.ClientNotification.create({
      notification_id: `meeting_reschedule_${m.id}_${Date.now()}`,
      title: t('admin:backoffice.meetings.notif.rescheduleTitle'),
      message: t('admin:backoffice.meetings.notif.rescheduleMessage', { name: m.prospect_name }),
      type: 'info',
    }).catch(() => undefined);
    if (calendlyUrl) window.open(calendlyUrl, '_blank', 'noopener');
  }, t('admin:backoffice.meetings.notif.rescheduleTitle'));

  const setStatus = (m: MeetingRow, status: string) => run(m, async () => {
    await a.entities.ParallelCall.update(m.id, { meeting_status: status, validation_history: history(m, `meeting_status:${status}`) });
  }, t('admin:backoffice.meetings.statusUpdated'));

  const now = Date.now();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const columns: DataTableColumn<MeetingRow>[] = [
    {
      id: 'when', header: t('admin:backoffice.meetings.columns.when'),
      cell: (m) => {
        const d = meetingAt(m);
        return (
          <div className="whitespace-nowrap">
            <div className="tabular-nums text-[var(--vf-text-1)]">{d ? fmt.format(d) : '—'}</div>
            {d && <div className="text-[12px] text-[var(--vf-text-3)]">{d.getTime() >= now ? t('admin:backoffice.meetings.stats.upcoming') : t('admin:backoffice.meetings.stats.past')}</div>}
          </div>
        );
      },
    },
    {
      id: 'prospect', header: t('admin:backoffice.meetings.columns.prospect'),
      cell: (m) => (
        <div className="min-w-0">
          <div className="font-medium text-[var(--vf-text-1)]">{m.prospect_name || '—'}</div>
          {m.prospect_company && <div className="text-[12px] text-[var(--vf-text-3)]">{m.prospect_company}</div>}
        </div>
      ),
    },
    {
      id: 'validation', header: t('admin:backoffice.meetings.columns.validation'),
      cell: (m) => t(`admin:backoffice.meetings.validationStatus.${m.client_validation_status && m.client_validation_status !== 'pending' ? m.client_validation_status : 'pending'}`, { defaultValue: m.client_validation_status || '' }),
    },
    {
      id: 'status', header: t('admin:backoffice.meetings.status_'), hideable: true,
      cell: (m) => (
        <Select value={m.meeting_status || 'scheduled'} onValueChange={(v) => { void setStatus(m, v); }} disabled={busy === m.id}>
          <SelectTrigger className="h-11 w-40" aria-label={t('admin:backoffice.meetings.statusOf', { name: m.prospect_name || '' })}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MEETING_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`admin:backoffice.meetings.status.${STATUS_KEY[s]}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      ),
    },
    {
      id: 'actions', header: <span className="sr-only">{t('admin:backoffice.meetings.columns.actions')}</span>, align: 'right',
      cell: (m) => {
        const d = meetingAt(m);
        const upcoming = !!d && d.getTime() >= now;
        const pending = !m.client_validation_status || m.client_validation_status === 'pending';
        const url = joinUrl(m);
        return (
          <div className="flex flex-wrap justify-end gap-2">
            {upcoming && url && (
              <Button asChild size="sm" variant="outline" className="min-h-11">
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-1 h-4 w-4" aria-hidden />{t('admin:backoffice.meetings.join')}
                </a>
              </Button>
            )}
            {upcoming && pending && (
              <>
                <Button size="sm" className="min-h-11" disabled={busy === m.id} onClick={() => { void accept(m); }}>{t('admin:backoffice.meetings.accept')}</Button>
                <Button size="sm" variant="outline" className="min-h-11" disabled={busy === m.id} onClick={() => { void decline(m); }}>{t('admin:backoffice.meetings.decline')}</Button>
              </>
            )}
            {upcoming && m.client_validation_status !== 'declined' && (
              <Button size="sm" variant="ghost" className="min-h-11" disabled={busy === m.id} onClick={() => { void reschedule(m); }}>{t('admin:backoffice.meetings.reschedule')}</Button>
            )}
          </div>
        );
      },
    },
  ];

  const pendingTotal = awaiting.query.isSuccess ? awaiting.rows.length : undefined;
  const TABS: MeetingsTab[] = ['pending', 'confirmed', 'all'];

  return (
    <div className="min-w-0 space-y-4">
      <div role="tablist" aria-label={t('admin:backoffice.meetings.title')} className="flex flex-wrap gap-1">
        {TABS.map((id) => (
          <Button
            key={id} type="button" role="tab" aria-selected={tab === id} className="min-h-11"
            variant={tab === id ? 'secondary' : 'ghost'} size="sm" onClick={() => selectTab(id)}
          >
            {t(`admin:backoffice.meetings.tabs.${id}`)}
            {id === 'pending' && typeof pendingTotal === 'number' && pendingTotal > 0 && (
              <span className="ml-2 rounded-full bg-[var(--vf-accent-bg)] px-2 text-[12px] tabular-nums text-[var(--vf-accent-text)]">{pendingTotal}</span>
            )}
          </Button>
        ))}
      </div>

      <DataTable<MeetingRow>
        caption={t(`admin:backoffice.meetings.tabs.${tab}`)}
        columns={columns}
        rows={rows}
        getRowId={(m) => m.id}
        status={shown.isLoading ? 'loading' : shown.isError && !shown.data ? 'error' : 'ready'}
        error={{ message: t('admin:backoffice.meetings.loadError'), code: codeOf(shown.error), onRetry: () => { void shown.refetch(); } }}
        empty={{ title: t(`admin:backoffice.meetings.empty.${tab}`) }}
        pagination={total !== null && (total > MEETINGS_PAGE_SIZE || page > 0) ? { page, pageSize: MEETINGS_PAGE_SIZE, total, onPageChange } : undefined}
        storageKey="portal-meetings"
      />
    </div>
  );
}
